// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React from 'react';
import './WarpGateVisualization.css';

/**
 * WarpGateVisualization - Advanced SVG-based warp gate portal effect
 * 
 * Creates a mesmerizing dimensional portal with:
 * - Multiple rotating rings at different speeds
 * - Pulsing energy core with gradient
 * - Spiraling particles being pulled into the vortex
 * - Glowing edges and distortion effects
 * - Optional tractor beam effect (for docked state)
 * - Cyberpunk magenta/cyan color scheme
 */
const WarpGateVisualization = ({ size = 100, discovered = true, showTractorBeam = false }) => {
  return (
    <div className="warp-gate-visualization" style={{ width: size, height: size }}>
      <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <defs>
          {/* Radial gradient for core */}
          <radialGradient id="coreGradient">
            <stop offset="0%" stopColor="#ff00ff" stopOpacity="1" />
            <stop offset="50%" stopColor="#ff00ff" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#8800ff" stopOpacity="0" />
          </radialGradient>

          {/* Radial gradient for rings */}
          <radialGradient id="ringGradient">
            <stop offset="0%" stopColor="#ff00ff" stopOpacity="0" />
            <stop offset="80%" stopColor="#ff00ff" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#ff00ff" stopOpacity="0" />
          </radialGradient>

          {/* Glow filter */}
          <filter id="glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>

          {/* Stronger glow for core */}
          <filter id="strongGlow">
            <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>

          {/* Turbulence for distortion effect */}
          <filter id="turbulence">
            <feTurbulence type="fractalNoise" baseFrequency="0.02" numOctaves="3" result="turbulence"/>
            <feDisplacementMap in2="turbulence" in="SourceGraphic" scale="3" xChannelSelector="R" yChannelSelector="G"/>
          </filter>
        </defs>

        {/* Outer glow aura */}
        <circle 
          cx="50" 
          cy="50" 
          r="45" 
          fill="url(#coreGradient)" 
          opacity="0.3"
          className="gate-aura"
        />

        {/* Rotating rings - outer to inner */}
        <g className="gate-rings">
          {/* Outer ring - slow rotation */}
          <circle 
            cx="50" 
            cy="50" 
            r="40" 
            fill="none" 
            stroke="#ff00ff" 
            strokeWidth="1.5" 
            opacity="0.6"
            filter="url(#glow)"
            className="gate-ring gate-ring-outer"
            strokeDasharray="5,10"
          >
            <animate
              attributeName="stroke-dashoffset"
              from="0"
              to="251.2"
              dur="20s"
              repeatCount="indefinite"
            />
          </circle>
          
          {/* Middle ring - medium rotation */}
          <circle 
            cx="50" 
            cy="50" 
            r="30" 
            fill="none" 
            stroke="#ff00ff" 
            strokeWidth="2" 
            opacity="0.7"
            filter="url(#glow)"
            className="gate-ring gate-ring-middle"
            strokeDasharray="8,8"
          >
            <animate
              attributeName="stroke-dashoffset"
              from="188.4"
              to="0"
              dur="15s"
              repeatCount="indefinite"
            />
          </circle>
          
          {/* Inner ring - fast rotation */}
          <circle 
            cx="50" 
            cy="50" 
            r="20" 
            fill="none" 
            stroke="#ff00ff" 
            strokeWidth="2.5" 
            opacity="0.8"
            filter="url(#glow)"
            className="gate-ring gate-ring-inner"
            strokeDasharray="10,5"
          >
            <animate
              attributeName="stroke-dashoffset"
              from="0"
              to="125.6"
              dur="10s"
              repeatCount="indefinite"
            />
          </circle>
        </g>

        {/* Tractor beam - dots traveling from ship to gate center (only when docked) */}
        {showTractorBeam && (
          <g className="gate-tractor-beam">
            {/* Create animated dots that travel from ship (right) to gate center */}
            {Array.from({ length: 8 }, (_, i) => (
              <circle
                key={i}
                cx="95"
                cy="50"
                r="1.5"
                fill="#00ffff"
                filter="url(#glow)"
                className="gate-beam-dot"
                style={{
                  animationDelay: `${i * 0.2}s`
                }}
              >
                <animate
                  attributeName="cx"
                  values="95;50"
                  dur="1.6s"
                  repeatCount="indefinite"
                  begin={`${i * 0.2}s`}
                />
                <animate
                  attributeName="opacity"
                  values="0;1;1;0"
                  keyTimes="0;0.1;0.9;1"
                  dur="1.6s"
                  repeatCount="indefinite"
                  begin={`${i * 0.2}s`}
                />
                <animate
                  attributeName="r"
                  values="1;1.5;1.5;2"
                  keyTimes="0;0.1;0.9;1"
                  dur="1.6s"
                  repeatCount="indefinite"
                  begin={`${i * 0.2}s`}
                />
              </circle>
            ))}
          </g>
        )}

        {/* Energy vortex lines - beams from edge to center */}
        <g className="gate-vortex">
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="360 50 50"
            to="0 50 50"
            dur="8s"
            repeatCount="indefinite"
          />
          {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map(angle => (
            <line
              key={angle}
              x1="50"
              y1="50"
              x2={50 + Math.cos((angle * Math.PI) / 180) * 45}
              y2={50 + Math.sin((angle * Math.PI) / 180) * 45}
              stroke="#00ffff"
              strokeWidth="1.5"
              opacity="0.6"
              className="gate-vortex-line"
              filter="url(#glow)"
            >
              <animate
                attributeName="opacity"
                values="0.2;0.8;0.2"
                dur="2s"
                repeatCount="indefinite"
                begin={`${angle / 360}s`}
              />
            </line>
          ))}
        </g>

        {/* Pulsing core */}
        <circle 
          cx="50" 
          cy="50" 
          r="12" 
          fill="url(#coreGradient)" 
          filter="url(#strongGlow)"
          className="gate-core"
        />

        {/* Center symbol */}
        <text
          x="50"
          y="50"
          textAnchor="middle"
          dominantBaseline="central"
          fontSize="16"
          fill="#ffffff"
          filter="url(#strongGlow)"
          className="gate-symbol"
        >
          {discovered ? '🌀' : '❓'}
        </text>

        {/* Distortion overlay (subtle) */}
        <circle 
          cx="50" 
          cy="50" 
          r="45" 
          fill="none" 
          stroke="#ff00ff" 
          strokeWidth="0.5" 
          opacity="0.2"
          filter="url(#turbulence)"
          className="gate-distortion"
        />
      </svg>
    </div>
  );
};

export default WarpGateVisualization;
